import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import axios from '../api/axios';
import { CheckCircle, XCircle, Clock, User, Briefcase, MessageSquare } from 'lucide-react';
import styles from './PositionApplications.module.css';
import { useAuth } from '../context/AuthContext';
import ContextualHelp from '../components/ContextualHelp';

interface Position {
  id: number;
  title: string;
  department: string;
}

interface ApplicationUser {
  id: number;
  username: string;
  email: string;
}

interface Application {
  id: number;
  userId: number;
  positionId: number;
  applicationMessage: string | null;
  status: number; // 1=Pending, 2=Approved, 3=Denied, 4=Withdrawn
  responseMessage: string | null;
  reviewedByUserId: number | null;
  createdAt: string;
  updatedAt: string | null;
  user: ApplicationUser;
  position: Position;
  reviewedBy: ApplicationUser | null;
}

const getStatusLabel = (status: number) => {
  switch (status) {
    case 1: return 'Pending';
    case 2: return 'Approved';
    case 3: return 'Denied';
    case 4: return 'Withdrawn';
    default: return 'Unknown';
  }
};

const getStatusColor = (status: number) => {
  switch (status) {
    case 1: return '#f59e0b'; // Pending - orange
    case 2: return '#10b981'; // Approved - green
    case 3: return '#ef4444'; // Denied - red
    case 4: return '#6b7280'; // Withdrawn - gray
    default: return '#6b7280';
  }
};

const PositionApplications: React.FC = () => {
  const { user } = useAuth();
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'denied'>('pending');
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const statusParam = filter === 'all' ? '' : `?status=${filter}`;
      const response = await axios.get(`/PositionApplications${statusParam}`);
      setApplications(response.data);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, [filter]);

  // 🔒 Authorization: Admin + CEO only
  const isCeo = (): boolean => {
    if (!user) return false;
    const titles = [
      user.primaryPositionTitle,
      user.secondaryPositionTitle,
      user.tertiaryPositionTitle
    ].filter(Boolean).map((t: string) => t.toLowerCase());
    
    return titles.includes('ceo') || titles.includes('chief executive officer');
  };

  const hasAccess = user?.role === 'Admin' || isCeo();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!hasAccess) {
    return <Navigate to="/home" replace />;
  }

  const handleApprove = async (appId: number) => {
    if (!responseMessage.trim()) {
      alert('Please provide a response message.');
      return;
    }

    try {
      setProcessing(true);
      await axios.put(`/PositionApplications/${appId}/approve`, {
        responseMessage: responseMessage.trim()
      });
      alert('Application approved successfully!');
      setSelectedApp(null);
      setResponseMessage('');
      fetchApplications();
    } catch (error: any) {
      console.error('Failed to approve application:', error);
      alert(error.response?.data || 'Failed to approve application.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeny = async (appId: number) => {
    if (!responseMessage.trim()) {
      alert('Please provide a reason for denial.');
      return;
    }

    try {
      setProcessing(true);
      await axios.put(`/PositionApplications/${appId}/deny`, {
        responseMessage: responseMessage.trim()
      });
      alert('Application denied.');
      setSelectedApp(null);
      setResponseMessage('');
      fetchApplications();
    } catch (error: any) {
      console.error('Failed to deny application:', error);
      alert(error.response?.data || 'Failed to deny application.');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <h1>
            <Briefcase size={32} />
            Position Applications
          </h1>
          <ContextualHelp featureArea="positions" />
        </div>
        <p className={styles.subtitle}>Review and manage position applications from members</p>
      </div>

      <div className={styles.filterBar}>
        <button
          className={`${styles.filterBtn} ${filter === 'pending' ? styles.active : ''}`}
          onClick={() => setFilter('pending')}
        >
          <Clock size={18} />
          Pending
        </button>
        <button
          className={`${styles.filterBtn} ${filter === 'approved' ? styles.active : ''}`}
          onClick={() => setFilter('approved')}
        >
          <CheckCircle size={18} />
          Approved
        </button>
        <button
          className={`${styles.filterBtn} ${filter === 'denied' ? styles.active : ''}`}
          onClick={() => setFilter('denied')}
        >
          <XCircle size={18} />
          Denied
        </button>
        <button
          className={`${styles.filterBtn} ${filter === 'all' ? styles.active : ''}`}
          onClick={() => setFilter('all')}
        >
          All
        </button>
      </div>

      {loading ? (
        <div className={styles.loading}>Loading applications...</div>
      ) : applications.length === 0 ? (
        <div className={styles.emptyState}>
          <Briefcase size={64} />
          <p>No {filter !== 'all' ? filter : ''} applications found.</p>
        </div>
      ) : (
        <div className={styles.applicationsList}>
          {applications.map(app => (
            <div key={app.id} className={styles.applicationCard}>
              <div className={styles.cardHeader}>
                <div className={styles.applicantInfo}>
                  <User size={20} />
                  <div>
                    <h3>{app.user.username}</h3>
                    <p className={styles.email}>{app.user.email}</p>
                  </div>
                </div>
                <span
                  className={styles.statusBadge}
                  style={{ backgroundColor: getStatusColor(app.status) }}
                >
                  {getStatusLabel(app.status)}
                </span>
              </div>

              <div className={styles.cardBody}>
                <div className={styles.positionInfo}>
                  <Briefcase size={18} />
                  <div>
                    <h4>{app.position.title}</h4>
                    <p>{app.position.department}</p>
                  </div>
                </div>

                {app.applicationMessage && (
                  <div className={styles.message}>
                    <MessageSquare size={16} />
                    <p>{app.applicationMessage}</p>
                  </div>
                )}

                <div className={styles.metadata}>
                  <span>Applied: {formatDate(app.createdAt)}</span>
                  {app.updatedAt && <span>Updated: {formatDate(app.updatedAt)}</span>}
                </div>

                {app.responseMessage && (
                  <div className={styles.response}>
                    <strong>Response:</strong>
                    <p>{app.responseMessage}</p>
                    {app.reviewedBy && <span>— {app.reviewedBy.username}</span>}
                  </div>
                )}
              </div>

              {app.status === 1 && (
                <div className={styles.cardActions}>
                  <button
                    className={styles.reviewBtn}
                    onClick={() => {
                      setSelectedApp(app);
                      setResponseMessage('');
                    }}
                  >
                    Review Application
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {selectedApp && (
        <div className={styles.modalOverlay} onClick={() => setSelectedApp(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Review Application</h2>
              <button className={styles.closeBtn} onClick={() => setSelectedApp(null)}>×</button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.reviewInfo}>
                <p><strong>Applicant:</strong> {selectedApp.user.username}</p>
                <p><strong>Position:</strong> {selectedApp.position.title}</p>
                <p><strong>Department:</strong> {selectedApp.position.department}</p>
                {selectedApp.applicationMessage && (
                  <div className={styles.applicantMessage}>
                    <strong>Their Message:</strong>
                    <p>{selectedApp.applicationMessage}</p>
                  </div>
                )}
              </div>

              <div className={styles.responseForm}>
                <label htmlFor="responseMessage">Your Response *</label>
                <textarea
                  id="responseMessage"
                  value={responseMessage}
                  onChange={(e) => setResponseMessage(e.target.value)}
                  placeholder="Provide feedback to the applicant..."
                  rows={4}
                  className={styles.textarea}
                />
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button
                className={styles.denyBtn}
                onClick={() => handleDeny(selectedApp.id)}
                disabled={processing || !responseMessage.trim()}
              >
                <XCircle size={18} />
                Deny
              </button>
              <button
                className={styles.approveBtn}
                onClick={() => handleApprove(selectedApp.id)}
                disabled={processing || !responseMessage.trim()}
              >
                <CheckCircle size={18} />
                Approve & Assign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PositionApplications;
